import React, { useState } from 'react';
import { FileSpreadsheet, Check, StickyNote, Info } from 'lucide-react';

// Static mockup — shows the EXISTING "combine multiple columns into Notes" mapping.
const HEADERS = ['id', 'full_name', 'phone_number', 'email', 'city', 'model', 'budget', 'timeline', 'source', 'remark'];
const SAMPLE = { model: 'FASTag 50 units', budget: '₹25,000', timeline: 'This month', remark: 'Very interested, follow up Mon' };

const FIELD_MAP = [
  ['Customer Name', 'full_name', true],
  ['Mobile', 'phone_number', true],
  ['Email', 'email', false],
  ['City', 'city', false],
];

export default function LeadNotesMappingPreview() {
  const [noteCols, setNoteCols] = useState(['model', 'budget', 'timeline', 'remark']);
  const toggle = (h) => setNoteCols(c => c.includes(h) ? c.filter(x => x !== h) : [...c, h]);
  const combined = noteCols.map(h => `${h}: ${SAMPLE[h] || `<${h} value>`}`).join('  |  ');

  return (
    <div className="min-h-screen bg-slate-100 p-6">
      <div className="max-w-3xl mx-auto space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6 text-emerald-600" /> Lead Mapping — Notes from multiple columns
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Map single fields to columns as usual, then tick <b>any number</b> of extra columns to combine into each lead's <b>Notes</b>.
          </p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
          <div>
            <p className="text-[11px] text-slate-500 mb-1">Match each app field to a sheet column</p>
            <div className="grid grid-cols-2 gap-2">
              {FIELD_MAP.map(([label, col, req]) => (
                <div key={label}>
                  <label className="text-[11px] text-slate-600">{label}{req && <span className="text-rose-500"> *</span>}</label>
                  <div className="h-9 text-sm border border-slate-300 rounded-md px-2 flex items-center bg-slate-50 text-slate-700">{col}</div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[11px] font-semibold text-slate-700 flex items-center gap-1.5"><StickyNote className="w-3.5 h-3.5 text-amber-500" /> Include these columns in the lead's Notes</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-1 border rounded-lg p-2">
              {HEADERS.map(h => {
                const on = noteCols.includes(h);
                return (
                  <label key={h} onClick={() => toggle(h)} className="flex items-center gap-2 text-xs cursor-pointer py-0.5">
                    <span className={`w-4 h-4 rounded border-2 flex items-center justify-center ${on ? 'bg-amber-500 border-amber-500' : 'bg-white border-slate-300'}`}>
                      {on && <Check className="w-3 h-3 text-white" />}
                    </span>
                    <span className="truncate text-slate-700">{h}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
            <p className="text-[11px] font-semibold text-emerald-800 mb-1">Live preview — what a lead's Notes will contain</p>
            <p className="text-xs text-emerald-900 font-mono break-words">{combined || '(no columns selected)'}</p>
          </div>

          <div className="flex items-start gap-1.5 text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-2 py-1.5">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Each selected column is added as <b>“column: value”</b>, joined by <b>“ | ”</b>. Only non-empty values are included. Order follows your selection.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
